import { ApiResponse, Company } from "../types";
import { apiClient } from "./apiClient";

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface LoginResponse {
  data: {
    token: string;
    user: {
      id: number;
      email: string;
      name: string;
      userType: string;
      companyId?: number;
      companies?: Company[];
    };
    company?: Company;
  };
  message: string;
  success: boolean;
}

export interface ResetPasswordData {
  currentPassword: string;
  newPassword: string;
}

export interface AuthService {
  login: (credentials: LoginCredentials) => Promise<ApiResponse<LoginResponse>>;
  logout: () => void;
  getToken: () => string | null;
  isAuthenticated: () => boolean;
  getUserData: () => LoginResponse["data"]["user"] | null;
  getCompanies: () => Company[];
  /** Substitui a lista de empresas no `user_data` (ex.: após GET /companies). */
  setUserCompanies: (companies: Company[]) => void;
  getCompanyData: () => Company | null;
  setCompanyData: (company: Company | null) => void;
  addCompanyToUser: (company: Company) => void;
  updateCompanyData: (companyData: Partial<Company>) => void;
  /** Update a company in the user's companies list and in current company data if it's selected */
  updateCompanyInList: (company: Company) => void;
  resetPassword: (data: ResetPasswordData) => Promise<ApiResponse<{ message: string }>>;
}

class AuthServiceImpl implements AuthService {
  private readonly tokenKey = "auth_token";
  private readonly userDataKey = "user_data";
  private readonly companyDataKey = "company_data";
  private readonly selectedCompanyIdKey = "selected_company_id";

  async login(
    credentials: LoginCredentials
  ): Promise<ApiResponse<LoginResponse>> {
    try {
      const response = await apiClient.post<LoginResponse>(
        "/auth/login",
        credentials,
        { requiresAuth: false }
      );

      const token = response.data.token;
      const user = response.data.user;
      const companies: Company[] =
        user.companies && user.companies.length > 0
          ? user.companies
          : response.data.company
            ? [response.data.company]
            : [];

      localStorage.setItem(this.tokenKey, token);
      localStorage.setItem(this.userDataKey, JSON.stringify(user));

      const selectedCompany =
        companies.find((c) => c.id === (user.companyId ?? null)) ??
        companies[0] ?? null;
      if (selectedCompany) {
        localStorage.setItem(
          this.companyDataKey,
          JSON.stringify(selectedCompany)
        );
        localStorage.setItem(
          this.selectedCompanyIdKey,
          String(selectedCompany.id)
        );
      }

      return {
        success: true,
        data: response,
      };
    } catch (error: any) {
      console.error("Login error:", error);
      return {
        success: false,
        error: error.message || "Network error. Please try again.",
      };
    }
  }

  logout(): void {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.userDataKey);
    localStorage.removeItem(this.companyDataKey);
    localStorage.removeItem(this.selectedCompanyIdKey);
    window.location.href = "/login";
  }

  getToken(): string | null {
    return localStorage.getItem(this.tokenKey);
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    return token !== null;
  }

  getUserData(): LoginResponse["data"]["user"] | null {
    const userData = localStorage.getItem(this.userDataKey);
    if (!userData) return null;

    try {
      return JSON.parse(userData);
    } catch {
      return null;
    }
  }

  getCompanies(): Company[] {
    const user = this.getUserData();
    const companies = user?.companies;
    if (Array.isArray(companies) && companies.length > 0) return companies;
    const fallback = localStorage.getItem(this.companyDataKey);
    if (fallback) {
      try {
        const parsed = JSON.parse(fallback) as Company;
        return parsed ? [parsed] : [];
      } catch {
        return [];
      }
    }
    return [];
  }

  setUserCompanies(companies: Company[]): void {
    const user = this.getUserData();
    if (!user) return;
    localStorage.setItem(this.userDataKey, JSON.stringify({ ...user, companies }));
  }

  getCompanyData(): Company | null {
    const fromStorage = localStorage.getItem(this.companyDataKey);
    if (fromStorage) {
      try {
        return JSON.parse(fromStorage);
      } catch {
        // fall through to companies fallback
      }
    }
    const companies = this.getCompanies();
    const selectedId = localStorage.getItem(this.selectedCompanyIdKey);
    if (selectedId) {
      const found = companies.find((c) => c.id === Number(selectedId));
      if (found) return found;
    }
    return companies[0] ?? null;
  }

  setCompanyData(company: Company | null): void {
    if (company) {
      localStorage.setItem(this.companyDataKey, JSON.stringify(company));
      localStorage.setItem(this.selectedCompanyIdKey, String(company.id));
    } else {
      localStorage.removeItem(this.companyDataKey);
      localStorage.removeItem(this.selectedCompanyIdKey);
    }
  }

  addCompanyToUser(company: Company): void {
    const user = this.getUserData();
    if (!user) return;
    const list = Array.isArray(user.companies) ? [...user.companies] : [];
    if (list.some((c) => c.id === company.id)) return;
    list.push(company);
    const updated = { ...user, companies: list };
    localStorage.setItem(this.userDataKey, JSON.stringify(updated));
  }

  updateCompanyData(updatedCompanyData: Partial<Company>): void {
    const currentCompanyData = this.getCompanyData();
    if (currentCompanyData) {
      const mergedCompanyData = {
        ...currentCompanyData,
        ...updatedCompanyData,
      };
      localStorage.setItem(
        this.companyDataKey,
        JSON.stringify(mergedCompanyData)
      );
    }
  }

  updateCompanyInList(company: Company): void {
    const user = this.getUserData();
    if (user) {
      const list = Array.isArray(user.companies) ? [...user.companies] : [];
      const inList = list.some((c) => c.id === company.id);
      const updated = inList
        ? list.map((c) => (c.id === company.id ? company : c))
        : [...list, company];
      if (JSON.stringify(updated) !== JSON.stringify(list)) {
        localStorage.setItem(
          this.userDataKey,
          JSON.stringify({ ...user, companies: updated })
        );
      }
    }
    const current = this.getCompanyData();
    if (current?.id === company.id) {
      this.setCompanyData(company);
    }
  }

  async resetPassword(data: ResetPasswordData): Promise<ApiResponse<{ message: string }>> {
    try {
      const response = await apiClient.post<{ message: string }>(
        "/auth/reset-password",
        data
      );

      return {
        success: true,
        data: response,
      };
    } catch (error: any) {
      console.error("Reset password error:", error);
      return {
        success: false,
        error: error.message || "Failed to reset password",
      };
    }
  }
}

// Export singleton instance
export const authService: AuthService = new AuthServiceImpl();
